import Icon from "../components/ui/Icon";
import HomeTabs from "../components/HomeTabs";
import EmptyState from "../components/EmptyState";
import ManagerCard from "../components/ManagerCard";
import { useOutletContext } from "react-router-dom";
import NewsSection from "../components/NewsSection";
import CategoryIcon from "../components/CatIconMapper";
import { highlightText } from "../utils/TextHighLighter";
import { useState, useEffect, useCallback } from "react";
import InstitutionCard from "../components/InstitutionCard";
import { useSearch } from "../features/header/hooks/useSearch";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSearchContext } from "../components/Header/SearchContext";
import { useHomeLayout } from "../features/layout/hooks/useHomeLayout";

import {
  getSearchResultData,
  normalizeSearchNewsItems,
} from "../utils/normalizeSearchNews";

export default function HomePage() {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(false);
  const [activePerson, setActivePerson] = useState<number | null>(null);
  const [activeProject, setActiveProject] = useState<number | null>(null);
  const [visiblePersons, setVisiblePersons] = useState(4);
  const [visibleProjects, setVisibleProjects] = useState(4);
  const [pendingMore, setPendingMore] = useState<
    "managers" | "institutions" | "news" | null
  >(null);
  const { query: searchQuery, clear } = useSearchContext();
  const hasSearchResults = searchQuery.trim() !== "";
  const { data, isLoading, error } = useHomeLayout();
  const {
    data: searchResponse,
    isLoading: isSearchLoading,
    error: searchError,
  } = useSearch(searchQuery);
  const isMobile = window.innerWidth < 640;
  const categories = data?.categories ?? [];
  const items = data?.items ?? [];
  const searchCategories = searchResponse?.results?.categories?.data ?? [];
  const searchPersons = searchResponse?.results?.persons?.data ?? [];
  const searchProjects = searchResponse?.results?.projects?.data ?? [];
  const {
    loadedPersons,
    loadMorePersons,
    loadingMorePersons,
    hasMorePersons,
    personsLoading,
    projects,
    loadMoreProjects: loadMore,
    loadingMoreProjects: loadingMore,
    hasMoreProjects: hasMore,
  } = useOutletContext<any>();
  const searchNewsItems = normalizeSearchNewsItems(
    getSearchResultData(searchResponse?.results?.news).length > 0
      ? getSearchResultData(searchResponse?.results?.news)
      : getSearchResultData(searchResponse?.results?.items),
  );
  const searchNewsTotal =
    searchResponse?.results?.news?.total ??
    searchResponse?.results?.items?.total ??
    searchNewsItems.length;
  const [searchParams, setSearchParams] = useSearchParams();
  const mainTab =
    (searchParams.get("tab") as "services" | "managements" | "institutions") ||
    "services";
  const setMainTab = useCallback(
    (tab: "services" | "managements" | "institutions") => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("tab", tab);
          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1280px)");
    const updateIsDesktop = () => setIsDesktop(media.matches);

    updateIsDesktop();
    media.addEventListener("change", updateIsDesktop);

    return () => media.removeEventListener("change", updateIsDesktop);
  }, []);

  useEffect(() => {
    if (!hasSearchResults || isSearchLoading) return;

    const currentTabHasResults =
      (mainTab === "services" && searchCategories.length > 0) ||
      (mainTab === "managements" && searchPersons.length > 0) ||
      (mainTab === "institutions" && searchProjects.length > 0);

    if (currentTabHasResults) return;

    if (searchCategories.length > 0) setMainTab("services");
    else if (searchPersons.length > 0) setMainTab("managements");
    else if (searchProjects.length > 0) setMainTab("institutions");
  }, [
    hasSearchResults,
    isSearchLoading,
    mainTab,
    searchCategories.length,
    searchPersons.length,
    searchProjects.length,
    setMainTab,
  ]);
  const displayCategories = hasSearchResults ? searchCategories : categories;
  const displayPersons = hasSearchResults
    ? searchPersons
    : isMobile
      ? loadedPersons.slice(0, visiblePersons)
      : loadedPersons;

  const displayProjects = hasSearchResults
    ? searchProjects
    : isMobile
      ? projects.slice(0, visibleProjects)
      : projects;

  const handlePersonsLoadMore = async () => {
    if (isMobile) {
      if (visiblePersons + 4 <= loadedPersons.length) {
        setVisiblePersons((p) => p + 4);
        return;
      }
    }

    await loadMorePersons();
  };
  const handleProjectsLoadMore = async () => {
    if (isMobile) {
      if (visibleProjects + 4 <= projects.length) {
        setVisibleProjects((p) => p + 4);
        return;
      }
    }

    await loadMore();
  };
  const searchHasResults =
    searchCategories.length > 0 ||
    searchPersons.length > 0 ||
    searchProjects.length > 0 ||
    searchNewsItems.length > 0;
  const activeLoading = hasSearchResults
    ? isSearchLoading
    : isLoading || personsLoading;
  const activeError = hasSearchResults ? searchError : error;
  const handlePersonClick = (index: number) => setActivePerson(index);
  const handleProjectClick = (index: number) => setActiveProject(index);
  const clearSearch = () => clear();
  const searchPreviewLimit = isDesktop ? 8 : 4;
  const buildSearchMoreUrl = (path: string) =>
    `${path}?q=${encodeURIComponent(searchQuery.trim())}`;
  const goToSearchMore = (target: "managers" | "institutions" | "news") => {
    const routes = {
      managers: "/managers",
      institutions: "/institutions",
      news: "/news",
    };

    setPendingMore(target);
    navigate(buildSearchMoreUrl(routes[target]));
  };

  return (
    <>
      <div className="w-full min-h-screen bg-green-01 pb-4 xl:pb-12">
        <div
          dir="rtl"
          className="min-h-[5px] w-[90%] xl:w-[70%] place-self-center"
        />
        <div className="w-full place-self-center font-kahroba mt-8 px-4 xl:px-0">
          {hasSearchResults ? (
            <div className="space-y-8 xl:w-[80%] place-self-center">
              <div className="w-full mb-6 p-4 bg-white/80 shadow-sm border border-green-02 rounded-2xl">
                <div className="flex items-center justify-between w-full gap-x-3">
                  <h3 className="truncate text-xs xl:text-sm text-green-05 max-w-64 xl:max-w-fit font-estedad font-semibold">
                    نتایج جستجو برای: "{searchQuery}"
                  </h3>
                  <button
                    onClick={clearSearch}
                    className="text-[10px] shrink-0 font-estedad px-2.5 py-1 bg-green-01 text-green-05 hover:bg-green-02 rounded-full transition-colors"
                  >
                    پاک کردن جستجو
                  </button>
                </div>
              </div>

              {activeLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="size-8 border-2 border-green-03 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  {searchCategories.length > 0 && (
                    <section>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-semibold text-green-05 font-estedad">
                          نتایج در خدمات ({searchCategories.length} مورد)
                        </h4>
                      </div>
                      <div className="grid grid-cols-4 md:grid-cols-6 gap-y-4 xl:gap-10 place-items-center [direction:rtl]">
                        {searchCategories.map((category, index) => (
                          <Link
                            key={category.id ?? category.name ?? index}
                            to={`/services/${category.id}`}
                            className="flex flex-col items-start gap-y-2 pb-2.5 rounded-[20px] xl:hover:bg-green-01 transition"
                          >
                            <div className="size-[70px] xl:size-[100px] flex items-center justify-center bg-white rounded-[25px] shadow-sm">
                              <CategoryIcon
                                id={category.id}
                                className="size-6 xl:size-9 text-green-05"
                              />
                            </div>
                            <h5 className="text-[12px] xl:text-sm text-green-05 text-center line-clamp-2">
                              {highlightText(category.name ?? "", searchQuery)}
                            </h5>
                          </Link>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* 🟢 بخش سرچ مدیران اصلاح شده با کامپوننت جدید */}
                  {searchPersons.length > 0 && (
                    <section>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-semibold text-green-05 font-estedad">
                          نتایج در مدیران ({searchPersons.length} مورد)
                        </h4>
                      </div>
                      <div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5"
                        dir="rtl"
                      >
                        {searchPersons
                          .slice(0, searchPreviewLimit)
                          .map((person: any, index: number) => (
                            <ManagerCard
                              key={`${person.id || "person"}-${index}`}
                              person={person}
                              index={index}
                              isActive={activePerson === index}
                              searchQuery={searchQuery}
                              onClick={handlePersonClick}
                            />
                          ))}
                      </div>
                      {searchPersons.length > searchPreviewLimit && (
                        <div className="w-full flex items-center justify-center mt-5">
                          <button
                            type="button"
                            onClick={() => goToSearchMore("managers")}
                            disabled={pendingMore === "managers"}
                            className="text-[11px] font-medium bg-green-02 text-complement-06 font-estedad px-4 py-1.5 rounded-[10px]"
                          >
                            {pendingMore === "managers" ? (
                              <span className="block size-3 border border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              "بیشتر"
                            )}
                          </button>
                        </div>
                      )}
                    </section>
                  )}

                  {/* 🟢 بخش سرچ موسسات اصلاح شده با کامپوننت جدید */}
                  {searchProjects.length > 0 && (
                    <section>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-semibold text-green-05 font-estedad">
                          نتایج در موسسات ({searchProjects.length} مورد)
                        </h4>
                      </div>
                      <div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5"
                        dir="rtl"
                      >
                        {searchProjects
                          .slice(0, searchPreviewLimit)
                          .map((project: any, index: number) => (
                            <InstitutionCard
                              key={`${project.id || "project"}-${index}`}
                              project={project}
                              index={index}
                              isActive={activeProject === index}
                              searchQuery={searchQuery}
                              onClick={handleProjectClick}
                            />
                          ))}
                      </div>
                      {searchProjects.length > searchPreviewLimit && (
                        <div className="w-full flex items-center justify-center mt-5">
                          <button
                            type="button"
                            onClick={() => goToSearchMore("institutions")}
                            disabled={pendingMore === "institutions"}
                            className="text-[11px] font-medium bg-green-02 text-complement-06 font-estedad px-4 py-1.5 rounded-[10px]"
                          >
                            {pendingMore === "institutions" ? (
                              <span className="block size-3 border border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              "بیشتر"
                            )}
                          </button>
                        </div>
                      )}
                    </section>
                  )}

                  {searchNewsItems.length > 0 && (
                    <section>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-semibold text-green-05 font-estedad">
                          نتایج در اخبار ({searchNewsTotal} مورد)
                        </h4>
                      </div>
                      <NewsSection
                        form="form1"
                        layout="grid"
                        showHeader={false}
                        showMedia={false}
                        newsData={searchNewsItems.slice(0, searchPreviewLimit)}
                        showEmptyState={false}
                      />
                      {searchNewsTotal > searchPreviewLimit && (
                        <div className="w-full flex items-center justify-center mt-5">
                          <button
                            type="button"
                            onClick={() => goToSearchMore("news")}
                            disabled={pendingMore === "news"}
                            className="text-[11px] font-medium bg-green-02 text-complement-06 font-estedad px-4 py-1.5 rounded-[10px]"
                          >
                            {pendingMore === "news" ? (
                              <span className="block size-3 border border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              "بیشتر"
                            )}
                          </button>
                        </div>
                      )}
                    </section>
                  )}

                  {!searchHasResults && (
                    <div className="text-center text-green-04 shadow-sm overflow-hidden rounded-[25px]">
                      <EmptyState />
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div>
              <HomeTabs
                mainTab={mainTab}
                setMainTab={setMainTab}
                searchQuery={searchQuery}
                hasSearchResults={hasSearchResults}
                activeLoading={activeLoading}
                activeError={activeError}
                displayCategories={displayCategories}
                displayPersons={displayPersons}
                displayProjects={displayProjects}
                isMobile={isMobile}
                activePerson={activePerson}
                activeProject={activeProject}
                onPersonClick={handlePersonClick}
                onProjectClick={handleProjectClick}
                showPersonsLoadMore={hasMorePersons}
                onPersonsLoadMore={handlePersonsLoadMore}
                isPersonsLoadingMore={loadingMorePersons}
                showInstitutionsLoadMore={hasMore}
                onInstitutionsLoadMore={handleProjectsLoadMore}
                isInstitutionsLoadingMore={loadingMore}
              />
              <div className="w-full text-center my-10 text-green-02.5">
                <Icon icon="Scroll" className="w-5" />
              </div>
              <div className="flex items-center justify-between w-full px-5 xl:px-14 mb-3">
                <h2 className="text-[16px] font-semibold text-accent-05">
                  آخرین اخبار
                </h2>
                <Link
                  to="/news"
                  className="text-[11px] w-fit font-medium bg-green-02 text-complement-06 font-estedad px-3 py-1 rounded-[10px]"
                >
                  بیشتر
                </Link>
              </div>
              <NewsSection
                form="form2"
                newsData={items}
                isLoadingOverride={isLoading}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
